import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-purple-900 text-white">
      {/* Main Footer */}
      <div className="w-full px-4 md:px-8 lg:px-16 py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 md:gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 bg-white rounded-lg flex items-center justify-center">
                  <img
                    src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/64095c3d-7a3b-4158-984f-cbf732794666_Prueba.jpg?v=17a98cf8306df21a873dae6ce81de0d3"
                    alt="San Lorenzo School Logo"
                    className="w-9 h-9 rounded object-contain"
                  />
                </div>
                <div>
                  <h3 className="font-serif font-bold text-lg leading-tight">San Lorenzo</h3>
                  <p className="text-[10px] text-white/60 tracking-[0.2em] uppercase">School</p>
                </div>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-sm">
                {t('footer.description')}
              </p>
              <div className="flex items-center gap-3">
                {[
                  { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/share/191PuHLgP8/' },
                  { icon: 'ri-instagram-line', href: 'https://www.instagram.com/sanlo_cr?igsh=ZGdhdzhpaWJmanVx' },
                ].map((social) => (
                  <a
                    key={social.icon}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-yellow-500 transition-colors duration-200"
                    aria-label="Social media"
                  >
                    <i className={`${social.icon} text-white text-sm`} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-2">
              <h4 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-5">
                {t('footer.quickLinks')}
              </h4>
              <ul className="space-y-3">
                {[
                  { to: '/nosotros', label: t('nav.about') },
                  { to: '/admisiones', label: t('nav.admissions') },
                  { to: '/contacto', label: t('nav.contact') },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-white/60 text-sm hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Programs */}
            <div className="lg:col-span-3">
              <h4 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-5">
                {t('footer.programs')}
              </h4>
              <ul className="space-y-3">
                {[
                  { to: '/programas', label: t('programs.preschool') },
                  { to: '/programas', label: t('programs.primary') },
                  { to: '/programas', label: t('programs.secondary') },
                  { to: '/admisiones', label: t('admissions.process.title') },
                ].map((link, idx) => (
                  <li key={idx}>
                    <Link
                      to={link.to}
                      className="text-white/60 text-sm hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="lg:col-span-3">
              <h4 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-5">
                {t('footer.contact')}
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-yellow-400 text-sm" />
                  </div>
                  <span className="text-white/60 text-sm leading-relaxed">
                    {t('footer.address')}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-yellow-400 text-sm" />
                  </div>
                  <a
                    href="tel:+50683495115"
                    className="text-white/60 text-sm hover:text-white transition-colors"
                  >
                    +506 8349 5115
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-yellow-400 text-sm" />
                  </div>
                  <a
                    href="mailto:admision@sanlorenzocr.com"
                    className="text-white/60 text-sm hover:text-white transition-colors"
                  >
                    {t('footer.email')}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-yellow-400 text-sm" />
                  </div>
                  <span className="text-white/60 text-sm leading-relaxed">
                    {t('footer.hours')}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="w-full px-4 md:px-8 lg:px-16 py-6">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-xs">
              © {new Date().getFullYear()} San Lorenzo School. {t('footer.rights')}
            </p>
            <div className="flex items-center gap-6">
              <span className="text-white/40 text-xs hover:text-white/60 cursor-pointer transition-colors">
                {t('footer.privacy')}
              </span>
              <span className="text-white/40 text-xs hover:text-white/60 cursor-pointer transition-colors">
                {t('footer.terms')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}